import { Injectable } from '@angular/core';

/**
 * Handles opening a browser print window for a rendered receipt template canvas.
 *
 * Responsibilities:
 *   - Freezing <canvas> elements (QR codes, etc.) as static <img> so their
 *     pixel data survives outerHTML serialisation.
 *   - Collecting all Angular component CSS from the current document.
 *   - Writing the self-contained HTML print window with the inline
 *     resetAndAdjustLayout() script that recalculates element positions
 *     entirely within the print rendering context.
 *
 * The caller is responsible for:
 *   - Ensuring the canvas is rendered at 100 % zoom before calling print().
 *   - Triggering any pending Angular measurement cycles (forceRemeasure) first.
 *   - Handling the "pop-up blocked" return value and showing a message.
 */
@Injectable({ providedIn: 'root' })
export class ReceiptTemplatePrintService {

  /**
   * Opens a print window for the supplied canvas element.
   *
   * @param canvasEl     The `.tc-canvas` HTMLElement to capture and print.
   * @param templateName Used as the `<title>` of the print window.
   * @returns `true` if the print window opened successfully; `false` if the
   *          browser blocked the pop-up.
   */
  print(canvasEl: HTMLElement, templateName: string): boolean {
    // ── Freeze <canvas> elements as static <img> ─────────────────────────────
    // <canvas> pixel data lives in GPU memory and is NOT serialised into
    // outerHTML.  Any component that renders into a <canvas> (e.g. the QR-code
    // library) will appear as a blank box in the print window unless we first
    // convert each canvas to a base64 <img>.  We swap, capture, then restore
    // so the live editor canvas is completely unaffected.
    type CanvasSwap = { parent: Element; img: HTMLImageElement; canvas: HTMLCanvasElement };
    const swapped: CanvasSwap[] = [];

    (Array.from(canvasEl.querySelectorAll('canvas')) as HTMLCanvasElement[]).forEach(c => {
      const parent = c.parentElement;   // capture BEFORE replaceChild removes c from DOM
      if (!parent) return;
      try {
        const img = document.createElement('img');
        img.src = c.toDataURL('image/png');
        img.style.cssText = c.style.cssText;
        img.setAttribute('width',  String(c.width));
        img.setAttribute('height', String(c.height));
        parent.replaceChild(img, c);   // c.parentElement becomes null after this
        swapped.push({ parent, img, canvas: c });
      } catch { /* skip cross-origin canvases */ }
    });

    // ── Snapshot <img> elements as data URLs ─────────────────────────────────
    // Images displayed on the canvas are fetched with Angular's HttpClient
    // (which attaches auth headers).  A fresh print window makes bare browser
    // requests that carry no auth headers, so those images would fail to load.
    // Snapshot each already-loaded <img> onto an off-screen <canvas> to bake
    // it into a base64 data URL — no extra HTTP requests needed in the print
    // window.  Skip any image that hasn't finished loading yet.
    type ImgSwap = { img: HTMLImageElement; originalSrc: string };
    const imgSwapped: ImgSwap[] = [];

    (Array.from(canvasEl.querySelectorAll('img')) as HTMLImageElement[]).forEach(img => {
      // Only snapshot images that have fully loaded and have real dimensions.
      if (!img.complete || img.naturalWidth === 0) return;
      // Skip images that are already data URLs (e.g. canvas snapshots above).
      if (img.src.startsWith('data:')) return;
      try {
        const offscreen = document.createElement('canvas');
        offscreen.width  = img.naturalWidth;
        offscreen.height = img.naturalHeight;
        const ctx = offscreen.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);
        const dataUrl = offscreen.toDataURL('image/png');
        imgSwapped.push({ img, originalSrc: img.src });
        img.src = dataUrl;
      } catch { /* skip cross-origin images that block canvas taint */ }
    });

    const canvasHtml = canvasEl.outerHTML;

    // Restore original canvas elements immediately after capture.
    swapped.forEach(({ parent, img, canvas }) => parent.replaceChild(canvas, img));

    // Restore original image src values.
    imgSwapped.forEach(({ img, originalSrc }) => { img.src = originalSrc; });

    // ── Collect all Angular component CSS ────────────────────────────────────
    // Angular's compiled styles use [_ngcontent-xxx] attribute selectors that
    // are present in both the CSS rules and the cloned outerHTML, so all
    // component styles transfer correctly into the print window without any
    // additional class mapping.
    let cssText = '';
    Array.from(document.styleSheets).forEach(sheet => {
      try {
        Array.from(sheet.cssRules).forEach(rule => { cssText += rule.cssText + '\n'; });
      } catch (_) { /* skip cross-origin sheets */ }
    });

    // ── Open print window ────────────────────────────────────────────────────
    const pw = window.open('', '_blank');
    if (!pw) return false;

    pw.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Print: ${templateName}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { background: white; }
    body { display: block; }
    /* 10 mm margin on every side of every printed page. */
    @page { margin: 10mm; }
    ${cssText}
    /* ── Print overrides ─────────────────────────────────────── */
    /* Un-clip the canvas so content renders across multiple pages. */
    .tc-canvas { overflow: visible !important; box-shadow: none !important; }
    /* Force the browser to render all colours, borders, and backgrounds
       exactly as on screen — without this Chrome/Edge suppress them in print. */
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  </style>
</head>
<body>
  ${canvasHtml}
  <script>
    /**
     * Recalculates all element positions entirely within the print rendering
     * context.  This is the authoritative layout pass — it runs after every
     * image has loaded in the print window, so any difference between screen
     * DPI / viewport and the print renderer is automatically accounted for.
     *
     * Algorithm (mirrors sortedElements in template-canvas.component.ts):
     *   1. Reset every element's top to its original template Y × scale.
     *   2. Measure actual rendered heights of auto-height elements.
     *   3. For each element whose original Y is >= an auto-height element's
     *      configured bottom, add the overflow to its top.
     *   4. Expand the canvas height to contain all pushed content.
     */
    function resetAndAdjustLayout() {
      var canvas = document.querySelector('.tc-canvas');
      if (!canvas) return;
      var scale = parseFloat(canvas.getAttribute('data-scale') || '3.7795');

      // Step 1 — reset all tops to original template Y values.
      var allEls = Array.from(document.querySelectorAll('[data-original-y]'));
      allEls.forEach(function(el) {
        var origY = parseFloat(el.getAttribute('data-original-y') || '0');
        el.style.top = (origY * scale).toFixed(1) + 'px';
      });

      // Step 2 — measure overflow of every auto-height element.
      var overflows = [];
      var autoEls = Array.from(document.querySelectorAll('[data-is-auto="true"]'));
      autoEls.forEach(function(el) {
        var origY   = parseFloat(el.getAttribute('data-original-y')       || '0');
        var configH = parseFloat(el.getAttribute('data-configured-height') || '0');
        var actualH = el.getBoundingClientRect().height;
        var overflowPx = Math.max(0, actualH - configH * scale);
        if (overflowPx > 1) {
          overflows.push({ configuredBottomMm: origY + configH, overflowPx: overflowPx });
        }
      });

      // Step 3 — push elements below each overflowing element down.
      if (overflows.length > 0) {
        allEls.forEach(function(el) {
          var origY   = parseFloat(el.getAttribute('data-original-y') || '0');
          var extraPx = overflows
            .filter(function(t) { return t.configuredBottomMm <= origY; })
            .reduce(function(sum, t) { return sum + t.overflowPx; }, 0);
          if (extraPx > 0) {
            el.style.top = ((origY * scale) + extraPx).toFixed(1) + 'px';
          }
        });
      }

      // Step 4 — expand canvas height to contain all pushed content.
      var canvasRect = canvas.getBoundingClientRect();
      var maxBottom  = canvas.offsetHeight;
      allEls.forEach(function(el) {
        var b = el.getBoundingClientRect().bottom - canvasRect.top;
        if (b > maxBottom) maxBottom = b;
      });
      var neededH = Math.ceil(maxBottom) + 16;
      if (neededH > canvas.offsetHeight) {
        canvas.style.height = neededH + 'px';
      }
    }

    function doPrint() {
      resetAndAdjustLayout();
      // Brief delay so the browser applies style changes before the print dialog.
      setTimeout(function() {
        window.print();
        window.onafterprint = function() { window.close(); };
      }, 50);
    }

    var imgs    = Array.from(document.images);
    var pending = imgs.filter(function(img) { return !img.complete; });
    if (pending.length === 0) {
      doPrint();
    } else {
      var done = 0;
      pending.forEach(function(img) {
        img.onload = img.onerror = function() {
          done++;
          if (done >= pending.length) doPrint();
        };
      });
    }
  <\/script>
</body>
</html>`);
    pw.document.close();

    return true;
  }
}
